import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import moment from 'moment';
import { changeCurrency, formatter } from '../../../constants';
import { fitLogoBox, printPdfDocument, renderPdfBlob } from './invoicePrintShared';

const styles = StyleSheet.create({
  page: {
    paddingTop: 48,
    paddingBottom: 64,
    paddingHorizontal: 28,
    fontSize: 9,
    color: '#111111',
    fontFamily: 'Helvetica',
  },
  printStamp: {
    position: 'absolute',
    top: 16,
    left: 28,
    right: 28,
  },
  printStampText: {
    fontSize: 9,
    color: '#111111',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  headerLeft: {
    width: '48%',
  },
  headerRight: {
    width: '48%',
    alignItems: 'flex-end',
  },
  logo: {
    height: 70,
    marginBottom: 6,
  },
  companyName: {
    fontSize: 12,
    marginBottom: 2,
  },
  companyAddress: {
    fontSize: 11,
  },
  title: {
    fontSize: 22,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'right',
    marginBottom: 8,
    lineHeight: 1.15,
  },
  meta: {
    fontSize: 12,
    textAlign: 'right',
    marginBottom: 2,
  },
  section: {
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    marginBottom: 6,
  },
  table: {
    width: '100%',
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderColor: '#000000',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  cell: {
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#000000',
    paddingVertical: 4,
    paddingHorizontal: 3,
    justifyContent: 'center',
  },
  cellText: {
    fontSize: 8,
    textAlign: 'center',
  },
  bold: {
    fontFamily: 'Helvetica-Bold',
  },
  paidNote: {
    fontSize: 7,
    textAlign: 'right',
    marginTop: 1,
  },
  nestedWrap: {
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  footer: {
    position: 'absolute',
    bottom: 18,
    left: 28,
    right: 28,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerBrand: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  pageNumber: {
    fontSize: 10,
    color: '#111111',
  },
  footerText: {
    fontSize: 11,
    marginRight: 8,
  },
  footerLogo: {
    width: 110,
    height: 24,
  },
});

const INVOICE_WIDTHS = ['13%', '18%', '13%', '14%', '14%', '14%', '14%'];
const PAYMENT_WIDTHS = ['16%', '10%', '16%', '14%', '14%', '14%', '16%'];
const LINE_WIDTHS = ['25%', '25%', '25%', '25%'];

function PdfText({ style, children }) {
  return (
    <Text style={style} hyphenationCallback={(word) => [word]}>
      {children}
    </Text>
  );
}

function moneyLabel(value) {
  const amount = String(value ?? '').replace('$', '').trim();
  return amount ? `$${amount}` : '';
}

function parseMoney(value) {
  const number = parseFloat(String(value ?? '').replace(/\$|,/g, ''));
  return Number.isFinite(number) ? number : 0;
}

function isInvoicePartiallyPaid(invoice) {
  const total = changeCurrency(invoice?.total);
  const balance = changeCurrency(invoice?.balance);
  const paid = total - balance;
  return balance > 0 && paid > 0.009;
}

function getInvoiceAmountPaid(invoice) {
  const total = changeCurrency(invoice?.total);
  const balance = changeCurrency(invoice?.balance);
  const paid =
    invoice?.discount != null && invoice?.discount !== ''
      ? Number(invoice.discount)
      : total - balance;
  return Number.isFinite(paid) ? Math.max(paid, 0) : 0;
}

function isPaymentPartial(paymentRow) {
  const balance = parseMoney(
    paymentRow?.invoice_balance ?? paymentRow?.payments?.[0]?.invoice_balance
  );
  const total = parseMoney(
    paymentRow?.invoice_total_amount ?? paymentRow?.payments?.[0]?.invoice_total_amount
  );
  return balance > 0 && total > balance;
}

function titleText({ onlyUnpaid, onlyReady, onlyPayments }) {
  if (onlyUnpaid) return 'OOP\nUNPAID INVOICES';
  if (onlyReady) return 'OOP\nREADY TO PAY INVOICES';
  if (onlyPayments) return 'OOP INVOICES\nPAYMENTS';
  return 'OOP INVOICES';
}

function Cell({ width, align, bold, children }) {
  return (
    <View style={[styles.cell, { width }]}>
      <PdfText style={[styles.cellText, { textAlign: align || 'center' }, bold ? styles.bold : null]}>
        {children}
      </PdfText>
    </View>
  );
}

function HeaderRow({ headers, widths }) {
  return (
    <View style={styles.row} wrap={false}>
      {headers.map((header, index) => (
        <Cell key={`${header}-${index}`} width={widths[index]} bold>
          {header}
        </Cell>
      ))}
    </View>
  );
}

function InvoiceSection({ section, labels }) {
  const hauling = section.rows.reduce((sum, invoice) => sum + parseMoney(invoice.hauling), 0);
  const wait = section.rows.reduce((sum, invoice) => sum + parseMoney(invoice.wait), 0);
  const total = section.rows.reduce((sum, invoice) => sum + parseMoney(invoice.total), 0);
  const balance = section.rows.reduce((sum, invoice) => sum + parseMoney(invoice.balance), 0);
  const headers = [
    labels.invoiceNo,
    labels.oopName,
    labels.dueDate,
    labels.hauling,
    labels.waitTime,
    labels.grandTotal,
    labels.balance,
  ];

  return (
    <View style={styles.section}>
      {section.showTitle ? <PdfText style={styles.sectionTitle}>{section.title}</PdfText> : null}
      <View style={styles.table}>
        <HeaderRow headers={headers} widths={INVOICE_WIDTHS} />
        {section.rows.length === 0 ? (
          <View style={styles.row} wrap={false}>
            <Cell width='100%'>{labels.empty}</Cell>
          </View>
        ) : (
          section.rows.map((invoice) => {
            const partial = section.showPartial && isInvoicePartiallyPaid(invoice);
            return (
              <View
                key={`${section.title}-${invoice.invoiceId || invoice.invoice}`}
                style={styles.row}
                wrap={false}
              >
                <Cell width={INVOICE_WIDTHS[0]}>
                  {`${invoice.invoice ?? ''}${partial ? ` (${labels.partial})` : ''}`}
                </Cell>
                <Cell width={INVOICE_WIDTHS[1]}>{invoice.oopName}</Cell>
                <Cell width={INVOICE_WIDTHS[2]}>
                  {invoice.dueDate ? moment(invoice.dueDate).format('MM/DD/YYYY') : ''}
                </Cell>
                <Cell width={INVOICE_WIDTHS[3]} align='right'>
                  {moneyLabel(invoice.hauling)}
                </Cell>
                <Cell width={INVOICE_WIDTHS[4]} align='right'>
                  {moneyLabel(invoice.wait)}
                </Cell>
                <Cell width={INVOICE_WIDTHS[5]} align='right'>
                  {moneyLabel(invoice.total)}
                </Cell>
                <View style={[styles.cell, { width: INVOICE_WIDTHS[6] }]}>
                  <PdfText style={[styles.cellText, { textAlign: 'right' }]}>
                    {moneyLabel(invoice.balance)}
                  </PdfText>
                  {partial ? (
                    <PdfText style={styles.paidNote}>
                      {`${labels.amountPaid}: ${formatter.format(getInvoiceAmountPaid(invoice))}`}
                    </PdfText>
                  ) : null}
                </View>
              </View>
            );
          })
        )}
        <View style={styles.row} wrap={false}>
          <Cell width={INVOICE_WIDTHS[0]} bold>
            {labels.total}
          </Cell>
          <Cell width={INVOICE_WIDTHS[1]} />
          <Cell width={INVOICE_WIDTHS[2]} />
          <Cell width={INVOICE_WIDTHS[3]} align='right' bold>
            {formatter.format(hauling)}
          </Cell>
          <Cell width={INVOICE_WIDTHS[4]} align='right' bold>
            {formatter.format(wait)}
          </Cell>
          <Cell width={INVOICE_WIDTHS[5]} align='right' bold>
            {formatter.format(total)}
          </Cell>
          <Cell width={INVOICE_WIDTHS[6]} align='right' bold>
            {formatter.format(balance)}
          </Cell>
        </View>
      </View>
    </View>
  );
}

function PaymentLines({ lines, labels }) {
  return (
    <View style={styles.row} wrap={false}>
      <View style={[styles.cell, styles.nestedWrap, { width: '100%' }]}>
        <View style={styles.table}>
          <HeaderRow
            headers={[labels.invoiceNo, labels.valueOfPayment, labels.reference, labels.paymentDate]}
            widths={LINE_WIDTHS}
          />
          {lines.map((line, index) => (
            <View key={line.id ?? `${line.invoice_no}-${index}`} style={styles.row} wrap={false}>
              <Cell width={LINE_WIDTHS[0]}>{line.invoice_no}</Cell>
              <Cell width={LINE_WIDTHS[1]}>{formatter.format(Number(line.amount) || 0)}</Cell>
              <Cell width={LINE_WIDTHS[2]}>{line.reference || 'N/A'}</Cell>
              <Cell width={LINE_WIDTHS[3]}>
                {line.date_created ? moment(line.date_created).format('LLL') : ''}
              </Cell>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
}

function PaymentsSection({ payments, showTitle, labels }) {
  const headers = [
    labels.oopName,
    labels.invoiceCount,
    labels.paymentSource,
    labels.reference,
    labels.confirmation,
    labels.amountReceived,
    labels.paymentDate,
  ];

  return (
    <View style={styles.section}>
      {showTitle ? <PdfText style={styles.sectionTitle}>{labels.payments}</PdfText> : null}
      <View style={styles.table}>
        <HeaderRow headers={headers} widths={PAYMENT_WIDTHS} />
        {payments.length === 0 ? (
          <View style={styles.row} wrap={false}>
            <Cell width='100%'>{labels.empty}</Cell>
          </View>
        ) : (
          payments.map((payment) => {
            const lines = payment.lines || [];
            const source = lines[0]?.payment_source || payment?.payments?.[0]?.payment_source || '';
            const reference = lines[0]?.reference || payment?.payments?.[0]?.reference || 'N/A';
            const confirmation =
              lines[0]?.confirmation_no || payment?.payments?.[0]?.confirmation_no || 'N/A';
            const partial = isPaymentPartial(payment);
            return (
              <View key={`${payment.oopCompacctnum}-${payment.id ?? payment.date_created}`}>
                <View style={styles.row} wrap={false}>
                  <Cell width={PAYMENT_WIDTHS[0]}>{payment.oopName}</Cell>
                  <Cell width={PAYMENT_WIDTHS[1]}>{lines.length}</Cell>
                  <Cell width={PAYMENT_WIDTHS[2]}>
                    {`${source}${partial ? ` (${labels.partial})` : ''}`}
                  </Cell>
                  <Cell width={PAYMENT_WIDTHS[3]}>{reference}</Cell>
                  <Cell width={PAYMENT_WIDTHS[4]}>{confirmation}</Cell>
                  <Cell width={PAYMENT_WIDTHS[5]}>{formatter.format(payment.displayAmount || 0)}</Cell>
                  <Cell width={PAYMENT_WIDTHS[6]}>
                    {payment.date_created ? moment(payment.date_created).format('LLL') : ''}
                  </Cell>
                </View>
                {lines.length > 0 ? <PaymentLines lines={lines} labels={labels} /> : null}
              </View>
            );
          })
        )}
      </View>
    </View>
  );
}

function OopInvoicesPrintPdf({
  logoSrc,
  logoWidth,
  logoHeight,
  loadzLogoSrc,
  companyName,
  companyAddress,
  jobLabel,
  jobNoLabel,
  projectLabel,
  projectName,
  printSections,
  unpaidInvoices,
  readyInvoices,
  payments,
  labels,
  printedAt,
}) {
  const onlyUnpaid = printSections.unpaid && !printSections.ready && !printSections.payments;
  const onlyReady = printSections.ready && !printSections.unpaid && !printSections.payments;
  const onlyPayments = printSections.payments && !printSections.unpaid && !printSections.ready;
  const jobLine = jobLabel != null && jobLabel !== '' ? `${jobNoLabel} ${jobLabel}` : '';
  const invoiceSections = [
    printSections.unpaid
      ? {
          title: labels.unpaid,
          rows: unpaidInvoices,
          showPartial: true,
          showTitle: !onlyUnpaid,
        }
      : null,
    printSections.ready
      ? {
          title: labels.ready,
          rows: readyInvoices,
          showPartial: false,
          showTitle: !onlyReady,
        }
      : null,
  ].filter(Boolean);

  return (
    <Document title='OOP Invoices'>
      <Page size='LETTER' style={styles.page}>
        <View style={styles.printStamp} fixed>
          <PdfText style={styles.printStampText}>{printedAt}</PdfText>
        </View>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {logoSrc ? (
              <Image
                src={logoSrc}
                style={[styles.logo, fitLogoBox(logoWidth, logoHeight, 70, 250)].filter(Boolean)}
              />
            ) : null}
            <PdfText style={styles.companyName}>{companyName}</PdfText>
            {companyAddress ? <PdfText style={styles.companyAddress}>{companyAddress}</PdfText> : null}
          </View>
          <View style={styles.headerRight}>
            <PdfText style={styles.title}>{titleText({ onlyUnpaid, onlyReady, onlyPayments })}</PdfText>
            {jobLine ? <PdfText style={styles.meta}>{jobLine}</PdfText> : null}
            <PdfText style={styles.meta}>
              {projectLabel}: {projectName || '-'}
            </PdfText>
          </View>
        </View>
        {invoiceSections.map((section) => (
          <InvoiceSection key={section.title} section={section} labels={labels} />
        ))}
        {printSections.payments ? (
          <PaymentsSection payments={payments} showTitle={!onlyPayments} labels={labels} />
        ) : null}
        <View style={styles.footer} fixed>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          />
          <View style={styles.footerBrand}>
            <PdfText style={styles.footerText}>{labels.poweredBy}</PdfText>
            {loadzLogoSrc ? <Image src={loadzLogoSrc} style={styles.footerLogo} /> : null}
          </View>
        </View>
      </Page>
    </Document>
  );
}

function oopInvoicesDocument(props) {
  const { companyLogoSrc, previewWindow, ...pdfProps } = props;
  return {
    companyLogoSrc,
    previewWindow,
    createDocument: (assets) => <OopInvoicesPrintPdf {...pdfProps} {...assets} />,
  };
}

export async function buildOopInvoicesPdfBlob(props) {
  const { companyLogoSrc, createDocument } = oopInvoicesDocument(props);
  return renderPdfBlob({ companyLogoSrc, createDocument });
}

export async function printOopInvoicesPdf(props) {
  const { previewWindow, companyLogoSrc, createDocument } = oopInvoicesDocument(props);
  await printPdfDocument({
    previewWindow,
    companyLogoSrc,
    frameId: 'oop-invoices-print-frame',
    frameTitle: 'OOP invoices',
    createDocument,
  });
}
